import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
// استيراد دالة تحميل الصور إلى Cloudinary
// import { uploadCloudinaryImage } from './cloudinaryUploader'; // Removed Cloudinary
import { uploadImageToImgBB } from './imgbbService';
import { uploadImageToImageKit } from './imagekitService';
// import { convertToBase64 } from '../utils/imageUtils'; // تم إزالة التخزين المحلي
import { ImageProtectionType } from '../utils/types';

/**
 * أنواع تخزين الصور المدعومة
 */
export enum ImageStorageType {
  // LOCAL = 'local', // تم إزالة التخزين المحلي
  // CLOUDINARY = 'cloudinary', // Removed Cloudinary
  IMGBB = 'imgbb',
  IMAGEKIT = 'imagekit',
  // CLOUDINARY_WITH_LOCAL_FALLBACK = 'cloudinary_with_local_fallback', // Removed Cloudinary
  // IMGBB_WITH_LOCAL_FALLBACK = 'imgbb_with_local_fallback', // تم إزالة التخزين المحلي
  // IMAGEKIT_WITH_LOCAL_FALLBACK = 'imagekit_with_local_fallback', // تم إزالة التخزين المحلي
  // CLOUDINARY_AND_IMGBB = 'cloudinary_and_imgbb', // Removed Cloudinary
  // CLOUDINARY_AND_IMAGEKIT = 'cloudinary_and_imagekit', // Removed Cloudinary
  IMGBB_AND_IMAGEKIT = 'imgbb_and_imagekit', // Upload to both, display from ImgBB by default (legacy)
  IMGBB_AND_IMAGEKIT_DUAL = 'imgbb_and_imagekit_dual', // Upload to both, allows choosing display source
  // ALL_SERVICES = 'all_services', // Removed Cloudinary
  // DISPLAY_FROM_CLOUDINARY = 'display_from_cloudinary', // Removed Cloudinary
  DISPLAY_FROM_IMGBB = 'display_from_imgbb', // Legacy or specific use case?
  DISPLAY_FROM_IMAGEKIT = 'display_from_imagekit' // Legacy or specific use case?
}

// Add type for preferred display source
export type PreferredDisplaySource = 'imgbb' | 'imagekit';

/**
 * واجهة إعدادات تخزين الصور
 */
export interface ImageStorageSettings {
  storageType: ImageStorageType;
  preferredDisplaySource?: PreferredDisplaySource; // Optional: Only relevant for DUAL types
  imageProtection: ImageProtectionType;
  watermarkText?: string;
  updatedAt?: Date;
}

// Define the structure for the upload result
export interface UploadResult {
  imgbbUrl?: string;
  imagekitUrl?: string;
  localUrl?: string; // For base64 fallback
  imageUrl?: string; // Added for the new logic
}

// الإعدادات الافتراضية
const DEFAULT_IMAGE_STORAGE_SETTINGS: ImageStorageSettings = {
  storageType: ImageStorageType.IMGBB_AND_IMAGEKIT, // تم تغيير الإعداد الافتراضي بعد إزالة التخزين المحلي
  imageProtection: ImageProtectionType.NONE, // بدون حماية افتراضيًا
  // No default preferred source needed here, handled in UI/logic
};

/**
 * الحصول على إعدادات تخزين الصور
 * @returns وعد بإعدادات تخزين الصور
 */
export const getImageStorageSettings = async (): Promise<ImageStorageSettings> => {
  try {
    // محاولة الحصول على الإعدادات من Firestore
    const settingsDoc = await getDoc(doc(db, 'settings', 'imageStorage'));
    console.log('جاري استرجاع إعدادات تخزين الصور من Firestore...');

    if (settingsDoc.exists()) {
      const data = settingsDoc.data() as ImageStorageSettings;
      
      // Validate storageType exists in enum, fallback if not
      const isValidStorageType = Object.values(ImageStorageType).includes(data.storageType);
      const validStorageType = isValidStorageType 
        ? data.storageType 
        : DEFAULT_IMAGE_STORAGE_SETTINGS.storageType;
      
      // التحقق من صحة نوع الحماية
      const isValidProtectionType = data.imageProtection && 
        Object.values(ImageProtectionType).includes(data.imageProtection);
      const validProtectionType = isValidProtectionType
        ? data.imageProtection
        : DEFAULT_IMAGE_STORAGE_SETTINGS.imageProtection;
      
      // في حالة عدم وجود نوع تخزين صالح، نسجل رسالة تحذير
      if (!isValidStorageType) {
        console.warn(`نوع تخزين غير صالح: ${data.storageType}، استخدام النوع الافتراضي: ${DEFAULT_IMAGE_STORAGE_SETTINGS.storageType}`);
      } else {
        console.log(`تم العثور على إعدادات تخزين الصور: ${validStorageType}`);
      }

      return {
        storageType: validStorageType,
        preferredDisplaySource: data.preferredDisplaySource,
        imageProtection: validProtectionType,
        watermarkText: data.watermarkText,
        updatedAt: data.updatedAt
      };
    }

    // إذا لم تكن الإعدادات موجودة، استخدم الإعدادات الافتراضية
    console.log(`لم يتم العثور على إعدادات تخزين الصور، استخدام الإعدادات الافتراضية: ${DEFAULT_IMAGE_STORAGE_SETTINGS.storageType}`);
    return DEFAULT_IMAGE_STORAGE_SETTINGS;
  } catch (error) {
    console.error('Error getting image storage settings:', error);
    console.log(`خطأ في الحصول على إعدادات تخزين الصور، استخدام الإعدادات الافتراضية: ${DEFAULT_IMAGE_STORAGE_SETTINGS.storageType}`);
    return DEFAULT_IMAGE_STORAGE_SETTINGS;
  }
};

/**
 * حفظ إعدادات تخزين الصور
 * @param settings إعدادات تخزين الصور
 * @returns وعد بنتيجة عملية الحفظ
 */
export const saveImageStorageSettings = async (settings: ImageStorageSettings): Promise<void> => {
  try {
    // Remove preferredDisplaySource if storage type doesn't need it
    const settingsToSave: Partial<ImageStorageSettings> = { ...settings };
    if (settings.storageType !== ImageStorageType.IMGBB_AND_IMAGEKIT_DUAL) {
      delete settingsToSave.preferredDisplaySource;
    }

    // إذا لم يتم تحديد نوع الحماية، استخدم القيمة الافتراضية
    if (!settingsToSave.imageProtection) {
      settingsToSave.imageProtection = DEFAULT_IMAGE_STORAGE_SETTINGS.imageProtection;
    }

    await setDoc(doc(db, 'settings', 'imageStorage'), {
      ...settingsToSave,
      updatedAt: new Date()
    });

    console.log('Image storage settings saved successfully');
  } catch (error) {
    console.error('Error saving image storage settings:', error);
    throw error;
  }
};

/**
 * تحميل صورة حسب إعدادات التخزين المحددة
 * @param file ملف الصورة المراد تحميله
 * @param folder المجلد (اختياري، يستخدم فقط مع ImageKit.io حاليًا)
 * @returns وعد بكائن يحتوي على روابط الصور المحتملة
 */
export const uploadImage = async (file: File, folder?: string): Promise<UploadResult> => {
  try {
    // الحصول على إعدادات تخزين الصور
    const settings = await getImageStorageSettings();
    console.log(`استخدام نوع تخزين الصور: ${settings.storageType} لتحميل الصورة: ${file.name}`);
    
    const result: UploadResult = {};

    // تحميل الصورة حسب نوع التخزين المحدد
    switch (settings.storageType) {
      case ImageStorageType.IMGBB:
        console.log('استخدام ImgBB للصور...');
        try {
          result.imgbbUrl = await uploadImageToImgBB(file);
          result.imageUrl = result.imgbbUrl; // تعيين الرابط الرئيسي
          console.log('تم تحميل الصورة بنجاح إلى ImgBB:', result.imgbbUrl);
          return result;
        } catch (error) {
          console.error('فشل التحميل إلى ImgBB:', error);
          throw error; // Re-throw as ImgBB only was selected
        }

      case ImageStorageType.IMAGEKIT:
        console.log('استخدام ImageKit.io للصور...');
        try {
          result.imagekitUrl = await uploadImageToImageKit(file, folder);
          result.imageUrl = result.imagekitUrl; // تعيين الرابط الرئيسي
          console.log('تم تحميل الصورة بنجاح إلى ImageKit.io:', result.imagekitUrl);
          return result;
        } catch (error) {
          console.error('فشل التحميل إلى ImageKit.io:', error);
          throw error; // Re-throw as ImageKit only was selected
        }

      case ImageStorageType.IMGBB_AND_IMAGEKIT: // Legacy: Upload both, return ImgBB URL primarily
        console.log('استخدام ImgBB و ImageKit.io معًا (العرض الافتراضي من ImgBB)...');
        try {
          // تحميل الصورة إلى ImgBB أولاً
          result.imgbbUrl = await uploadImageToImgBB(file);
          result.imageUrl = result.imgbbUrl; // استخدام ImgBB كمصدر افتراضي
          console.log('تم تحميل الصورة بنجاح إلى ImgBB:', result.imgbbUrl);
          
          try {
            // ثم محاولة تحميلها إلى ImageKit.io أيضًا
            result.imagekitUrl = await uploadImageToImageKit(file, folder);
            console.log('تم تحميل الصورة بنجاح إلى ImageKit.io أيضًا:', result.imagekitUrl);
          } catch (imagekitError) {
            console.error('فشل التحميل الثانوي إلى ImageKit.io:', imagekitError);
            // Continue, as ImgBB succeeded
          }
        } catch (imgbbError) {
          console.error('فشل التحميل الأولي إلى ImgBB، محاولة ImageKit.io بدلاً من ذلك:', imgbbError);
          try {
            result.imagekitUrl = await uploadImageToImageKit(file, folder);
            result.imageUrl = result.imagekitUrl; // استخدام ImageKit.io كبديل
            console.log('تم تحميل الصورة بنجاح إلى ImageKit.io كبديل:', result.imagekitUrl);
          } catch (imagekitError) {
            console.error('فشل التحميل إلى ImageKit.io أيضًا:', imagekitError);
            throw new Error('فشل التحميل إلى كل من ImgBB و ImageKit.io');
          }
        }
        return result;

      case ImageStorageType.IMGBB_AND_IMAGEKIT_DUAL:
        console.log('استخدام ImgBB و ImageKit.io معًا (تحميل مزدوج)...');
        
        // استخدام Promise.allSettled للتحميل المتوازي
        const uploads = await Promise.allSettled([
          uploadImageToImgBB(file).catch(err => {
            console.error('فشل التحميل المتزامن إلى ImgBB:', err);
            return undefined; // Ensure failure returns undefined, not rejection
          }),
          uploadImageToImageKit(file, folder).catch(err => {
            console.error('فشل التحميل المتزامن إلى ImageKit.io:', err);
            return undefined; // Ensure failure returns undefined
          })
        ]);

        // تعيين النتائج
        result.imgbbUrl = uploads[0].status === 'fulfilled' ? uploads[0].value : undefined;
        result.imagekitUrl = uploads[1].status === 'fulfilled' ? uploads[1].value : undefined;

        // اختيار الرابط الرئيسي حسب الإعدادات أو توفر الروابط
        if (settings.preferredDisplaySource === 'imgbb' && result.imgbbUrl) {
          result.imageUrl = result.imgbbUrl;
          console.log('تم اختيار ImgBB كمصدر رئيسي للعرض حسب الإعدادات المفضلة');
        } else if (settings.preferredDisplaySource === 'imagekit' && result.imagekitUrl) {
          result.imageUrl = result.imagekitUrl;
          console.log('تم اختيار ImageKit.io كمصدر رئيسي للعرض حسب الإعدادات المفضلة');
        } else if (result.imgbbUrl) {
          result.imageUrl = result.imgbbUrl;
          console.log('تم اختيار ImgBB كمصدر رئيسي للعرض (افتراضي)');
        } else if (result.imagekitUrl) {
          result.imageUrl = result.imagekitUrl;
          console.log('تم اختيار ImageKit.io كمصدر رئيسي للعرض (لعدم توفر ImgBB)');
        }

        // التحقق من نجاح التحميل لخدمة واحدة على الأقل
        if (!result.imgbbUrl && !result.imagekitUrl) {
          console.error('فشل التحميل المزدوج إلى كل من ImgBB و ImageKit.io.');
          throw new Error('فشل التحميل إلى جميع خدمات تخزين الصور');
        } else {
          // تسجيل رسائل تحذير لحالات الفشل الجزئي
          if (!result.imgbbUrl) {
            console.warn('فشل التحميل إلى ImgBB، ولكن تم التحميل إلى ImageKit.io.');
          } else if (!result.imagekitUrl) {
            console.warn('فشل التحميل إلى ImageKit.io، ولكن تم التحميل إلى ImgBB.');
          } else {
            console.log('تم التحميل المزدوج بنجاح إلى ImgBB و ImageKit.io.');
          }
        }
        return result;

      case ImageStorageType.DISPLAY_FROM_IMGBB:
        console.warn('DISPLAY_FROM_IMGBB: سيتم استخدام ImgBB فقط');
        try {
          result.imgbbUrl = await uploadImageToImgBB(file);
          result.imageUrl = result.imgbbUrl;
          console.log('تم تحميل الصورة بنجاح إلى ImgBB:', result.imgbbUrl);
          return result;
        } catch (imgbbError) {
          console.error('فشل التحميل إلى ImgBB:', imgbbError);
          throw imgbbError;
        }

      case ImageStorageType.DISPLAY_FROM_IMAGEKIT:
        console.warn('DISPLAY_FROM_IMAGEKIT: سيتم استخدام ImageKit.io فقط');
        try {
          result.imagekitUrl = await uploadImageToImageKit(file, folder);
          result.imageUrl = result.imagekitUrl;
          console.log('تم تحميل الصورة بنجاح إلى ImageKit.io:', result.imagekitUrl);
          return result;
        } catch (imagekitError) {
          console.error('فشل التحميل إلى ImageKit.io:', imagekitError);
          throw imagekitError;
        }

      default:
        console.warn(`نوع تخزين غير معروف: ${settings.storageType}. استخدام ImgBB و ImageKit.io معًا...`);
        // استخدام الوضع المزدوج كبديل
        return await uploadImage(file, folder);
    }
  } catch (error) {
    console.error('خطأ عام في وظيفة تحميل الصور:', error);
    throw error;
  }
};

/**
 * تحميل مجموعة صور حسب إعدادات التخزين المحددة
 * @param files مصفوفة ملفات الصور المراد تحميلها
 * @param folder المجلد (اختياري)
 * @returns وعد بمصفوفة كائنات نتائج التحميل
 */
export const uploadMultipleImages = async (files: File[], folder?: string): Promise<UploadResult[]> => {
  try {
    // إنشاء مصفوفة وعود لتحميل الصور بشكل متوازٍ
    const uploadPromises = files.map(file => uploadImage(file, folder));
    // انتظار اكتمال جميع عمليات التحميل
    const results = await Promise.all(uploadPromises);
    return results;
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    throw error;
  }
};

// Helper function to get the display URL based on settings and result
export const getDisplayUrl = (result: UploadResult, settings: ImageStorageSettings): string | undefined => {
  if (settings.storageType === ImageStorageType.IMGBB_AND_IMAGEKIT_DUAL) {
    if (settings.preferredDisplaySource === 'imgbb') {
      return result.imgbbUrl ?? result.imagekitUrl ?? result.localUrl;
    } else if (settings.preferredDisplaySource === 'imagekit') {
      return result.imagekitUrl ?? result.imgbbUrl ?? result.localUrl;
    }
    // Default if no preference set (shouldn't happen if settings UI is correct)
    return result.imgbbUrl ?? result.imagekitUrl ?? result.localUrl;
  }

  // Handle other storage types (adjust as needed based on primary URL)
  if (result.imgbbUrl) return result.imgbbUrl;
  if (result.imagekitUrl) return result.imagekitUrl;
  if (result.localUrl) return result.localUrl;

  return undefined;
};

export default {
  ImageStorageType,
  getImageStorageSettings,
  saveImageStorageSettings,
  uploadImage,
  uploadMultipleImages,
  ImageProtectionType
};
